import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useVehicleTypes, useVehicleBrands, useUsersByRole, useTrailerAssociation } from "../../hooks/useVehicleQuery";
import { usePhoneCountryCodes } from "../../hooks/userComplementaryQuery";
import { useOperations } from "../../hooks/useDocumentationVehicleQuery";
import { useSearchablePagination } from "../../hooks/search-and-pagination/useSearchablePagination";
import { useVehicleSettingsOperations } from "../../hooks/useVehicleSettingsOperations"

import CustomInputField from "../../components/ui/inputs/CustomInputField";
import CustomDatePicker from "../../components/ui/CustomDatePicker";
import VehicleStreamingMediaType from "../../utils/VehicleStreamingMedia";
import { ReactComponent as SaveIcon } from "../../assets/svg/components/save.svg";
import { ReactComponent as CancelIcon } from "../../assets/svg/view/close_circle.svg";
import SortDropdown from "../../components/ui/dropdawn/SortDropdown";
import TrailerSelector from "../../components/ui/dropdawn/TrailerSelector";
import TrailerAssociationModal from "../../components/ui/modals/TrailerAssociationModal";
import "../../assets/scss/components/vehicle/VehicleProfileEdit.scss";

const VehicleProfileEdit = ({ 
  vehicle, 
  theme, 
  onSave, 
  onCancel, 
  isRelatedVehicle 
}) => {
  const [formData, setFormData] = useState({
    // Datos básicos del vehículo
    plate: vehicle?.plate || "",
    vehicleTypeId: vehicle?.vehicle_type_id || "",
    brandId: vehicle?.brand_id || "",
    model: vehicle?.model || "",
    year: vehicle?.year || "",
    color: vehicle?.color || "",
    capacity: vehicle?.capacity || "",
    nationalityId: vehicle?.nationality_id || "",
    
    // Teléfonos del vehículo (editables)
    primaryPhoneNumber: vehicle?.primary_phone_number || "",
    primaryPhoneCountryId: vehicle?.primary_phone_country_id || "",
    secondaryPhoneNumber: vehicle?.second_phone || "",
    secondaryPhoneCountryId: vehicle?.secondary_phone_country_id || "",
    
    // Fechas de contrato
    contractStartDate: vehicle?.contract_start_date || "",
    contractEndDate: vehicle?.contract_end_date || "",
    
    // Relaciones (solo IDs, no datos personales)
    ownerId: vehicle?.owner_id || "",
    operatorId: vehicle?.operator_id || "",

    // NUEVOS CAMPOS: Operación
    operationId: vehicle?.operation_id || "",
    belongsToOperation: vehicle?.belongs_to_operation || false,
    gpsProviderId: vehicle?.gps_provider_id || "",
  });

  // NUEVOS ESTADOS PARA GESTIÓN DE TRAILERS
  const [selectedTrailer, setSelectedTrailer] = useState(null);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [trailerConflictData, setTrailerConflictData] = useState(null);

const { useSatelliteProviders } = useVehicleSettingsOperations();
const { data: providersData, isLoading: providersLoading } = useSatelliteProviders()
  // Hooks para datos de catálogos
  const { data: typesData } = useVehicleTypes();
  const vehicleTypes = typesData?.data || [];

  const { data: countryCodes } = usePhoneCountryCodes();
  const countryCodesData = countryCodes?.data || [];

  // Operaciones
  const { data: operationsData, isLoading: operationsLoading } = useOperations({ limit: 100 });
  const operations = useMemo(() => operationsData?.data || [], [operationsData]);

  // Gestión de trailers
  const { associateTrailer, disassociateTrailer, isAssociating, isDisassociating } = useTrailerAssociation(vehicle.id);

  // Búsquedas paginadas para selección de personas
  const brandsSearch = useSearchablePagination(useVehicleBrands, [], {
    itemsPerPage: 10,
    enableSearch: true,
    enablePagination: true
  });

  const operatorsSearch = useSearchablePagination(useUsersByRole, [5], {
    itemsPerPage: 10,
    enableSearch: true,
    enablePagination: true
  });

  const ownersSearch = useSearchablePagination(useUsersByRole, [6], {
    itemsPerPage: 10,
    enableSearch: true,
    enablePagination: true
  });

  // Determinar si el tipo de vehículo requiere operación
  const requiresOperation = useMemo(() => {
    const currentType = vehicleTypes.find(type => type.id === formData.vehicleTypeId);
    if (!currentType) return false;
    
    const typesWithOperation = ["TRACTOCAMION", "CAMA ALTA", "CARGADOR"];
    return typesWithOperation.includes(currentType.name?.toUpperCase());
  }, [formData.vehicleTypeId, vehicleTypes]);

  // Funciones de utilidad para formateo (solo lectura)
  const formatPhoneNumber = (countryCode, phoneNumber) => {
    if (!phoneNumber) return "No disponible";
    return `${countryCode || ''} ${phoneNumber}`.trim();
  };

  const formatFullName = (person) => {
    if (!person) return "No asignado";
    const { first_name, first_last_name, second_last_name } = person;
    return `${first_name || ''} ${first_last_name || ''} ${second_last_name || ''}`.trim();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No disponible";
    try {
      return new Date(dateString).toLocaleDateString('es-CO');
    } catch {
      return "Fecha inválida";
    }
  };

  // Determinar si debe mostrar sección de operador
  const shouldShowOperatorSection = () => {
    return !vehicle.is_trailer;
  };

  // Determinar tipo de vehículo para título
  const getVehicleTypeTitle = () => {
    if (isRelatedVehicle) {
      return vehicle.is_trailer ? "Trailer Relacionado" : "Cabezote Relacionado";
    }
    return vehicle.is_trailer ? "Trailer" : "Vehículo";
  };

  // Handlers - ACTUALIZADO CON LÓGICA DE OPERACIÓN
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      // NUEVA LÓGICA: Manejo especial para operación
      if (field === "belongsToOperation") {
        if (!value) {
          newData.operationId = "";
        }
      }

      if (field === "operationId") {
        if (value && value !== "") {
          newData.belongsToOperation = true;
        }
      }

      return newData;
    });
  };

  // HANDLERS PARA GESTIÓN DE TRAILERS
  const handleTrailerSelection = (trailer) => {
    if (!trailer) {
      // Desasociar trailer
      setSelectedTrailer(null);
      return;
    }

    if (trailer.is_associated && trailer.id !== vehicle.relatedVehicle?.data?.id) {
      // Trailer ya está asociado, mostrar modal de confirmación
      setSelectedTrailer(trailer);
      setTrailerConflictData({
        associatedToVehicle: trailer.associated_to_vehicle_plate,
        associatedToVehicleId: trailer.associated_to_vehicle_id
      });
      setShowTrailerModal(true);
    } else {
      setSelectedTrailer(trailer);
    }
  };

  const handleTrailerAssociation = async (forceAssociation = false) => {
    try {
      if (!selectedTrailer) {
        // Desasociar trailer actual
        await disassociateTrailer();
      } else {
        // Asociar nuevo trailer
        await associateTrailer({
          trailerId: selectedTrailer.id,
          forceAssociation
        });
      }
      
      setShowTrailerModal(false);
      setTrailerConflictData(null);
      setSelectedTrailer(null);
    } catch (error) {
      if (error.conflictData?.isAssociated && !forceAssociation) {
        setTrailerConflictData(error.conflictData);
        setShowTrailerModal(true);
      }
    }
  };

  // VALIDACIÓN ACTUALIZADA CON OPERACIÓN
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar datos requeridos
    if (!formData.plate.trim()) {
      alert("La placa es requerida");
      return;
    }

    if (!formData.vehicleTypeId) {
      alert("El tipo de vehículo es obligatorio");
      return;
    }

    // NUEVA VALIDACIÓN: Operación para tipos que la requieren
    if (requiresOperation && formData.belongsToOperation && !formData.operationId) {
      alert("Debe seleccionar una operación para este tipo de vehículo");
      return;
    }
    if (vehicle.is_trailer || vehicle.vehicle_type_id === 'fc52271b-d9a9-47ac-b734-8aaa38bf5edf') {
    dataToSend.gpsProviderId = null;
  }

    // Preparar datos para envío
    const dataToSend = { ...formData };
    
    // Limpiar campos vacíos
    Object.keys(dataToSend).forEach(key => {
      if (dataToSend[key] === "" || dataToSend[key] === null) {
        dataToSend[key] = null;
      }
    });

    // Enviar datos del vehículo
    onSave(dataToSend);
  };

  // Opciones para dropdowns
  const brandOptions = brandsSearch.data.map((brand) => ({
    value: brand.id,
    label: brand.name,
  }));

  const operatorOptions = operatorsSearch.data.map((operator) => ({
    value: operator.id,
    label: `${operator.first_name} ${operator.first_last_name} ${operator.second_last_name || ''}`.trim(),
  }));

  const ownerOptions = ownersSearch.data.map((owner) => ({
    value: owner.id,
    label: `${owner.first_name} ${owner.first_last_name} ${owner.second_last_name || ''}`.trim(),
  }));

  const vehicleTypeOptions = vehicleTypes.map((type) => ({
    value: type.id,
    label: type.name,
  }));

  const countryOptions = countryCodesData.map((country) => ({
    value: country.id,
    label: `${country.country_code} (${country.country_name})`,
    country_code: country.country_code,
  }));

  const nationalityOptions = countryCodesData.map((country) => ({
    value: country.id,
    label: country.country_name,
  }));

  // Operaciones
  const operationOptions = [
    { value: "", label: "Sin operación específica" },
    ...operations.map(op => ({
      value: op.id,
      label: op.name
    }))
  ];
// Agregar opciones de GPS:
const gpsProviderOptions = [
  { value: "", label: "Sin proveedor GPS" },
  ...(providersData?.data || []).map((provider) => ({
    value: provider.id,
    label: provider.provider_name.charAt(0).toUpperCase() + provider.provider_name.slice(1),
  }))
];
  return (
    <div className={`GM__${theme}-vehicle-profile-edit`}>
      <form onSubmit={handleSubmit} className={`GM__${theme}-edit-form`}>
        {/* Botones de acción */}
        <div className={`GM__${theme}-action-buttons`}>
          <button 
            type="submit" 
            className={`GM__${theme}-save-button`}
            title="Guardar cambios"
            disabled={isAssociating || isDisassociating}
          >
            <SaveIcon />
            {isAssociating || isDisassociating ? "Procesando..." : "Guardar"}
          </button>
          <button 
            type="button" 
            onClick={onCancel}
            className={`GM__${theme}-cancel-button`}
            title="Cancelar edición"
            disabled={isAssociating || isDisassociating}
          >
            <CancelIcon />
            Cancelar
          </button>
        </div>

        <div className={`GM__${theme}-edit-container`}>
          {/* Header con imagen y campos principales */}
          <div className={`GM__${theme}-edit-header`}>
            <div className={`GM__${theme}-vehicle-image-container`}>
              <div className={`GM__${theme}-image-circle`}>
                <VehicleStreamingMediaType
                  url={vehicle.id} 
                  type="vehiclePhoto" 
                  className={`GM__${theme}-vehicle-image`}
                />
              </div>
            </div>
            
            <div className={`GM__${theme}-header-content`}>
              <h2 className={`GM__${theme}-vehicle-title`}>
                Editar {getVehicleTypeTitle()}
              </h2>
              <div className={`GM__${theme}-title-underline`}></div>
              <div className={`GM__${theme}-header-fields`}>
                <div className={`GM__${theme}-field-group`}>
                  <div className={`GM__${theme}-field`}>
                    <span className={`GM__${theme}-field-label`}>Placa:</span>
                    <CustomInputField
                      value={formData.plate}
                      onChange={(e) => handleInputChange('plate', e.target.value)}
                      theme={theme}
                      className={`GM__${theme}-compact-input`}
                      placeholder="Ingrese placa"
                      required
                    />
                  </div>
                  <div className={`GM__${theme}-field`}>
                    <span className={`GM__${theme}-field-label`}>Tipo de vehículo:</span>
                    <SortDropdown
                      value={formData.vehicleTypeId}
                      onChange={(value) => handleInputChange('vehicleTypeId', value)}
                      options={vehicleTypeOptions}
                      theme={theme}
                      placeholder="Seleccione tipo"
                      className={`GM__${theme}-compact-dropdown`}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* NUEVA SECCIÓN: Configuración de Operación */}
          <div className={`GM__${theme}-operation-section`}>
            <h3 className={`GM__${theme}-section-title`}>Configuración de Operación</h3>
            <div className={`GM__${theme}-operation-content`}>
              {/* Toggle para pertenencia a operación */}
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-checkbox-field`}>
                  <input
                    type="checkbox"
                    id="belongsToOperation"
                    checked={formData.belongsToOperation}
                    onChange={(e) => handleInputChange("belongsToOperation", e.target.checked)}
                    className={`GM__${theme}-checkbox`}
                  />
                  <label htmlFor="belongsToOperation" className={`GM__${theme}-checkbox-label`}>
                    Este vehículo pertenece a una operación específica
                  </label>
                </div>
              </div>

              {/* Selector de operación */}
              {formData.belongsToOperation && (
                <div className={`GM__${theme}-operation-selector`}>
                  <div className={`GM__${theme}-field-group`}>
                    <div className={`GM__${theme}-field-full`}>
                      <span className={`GM__${theme}-field-label`}>Operación:</span>
                      <SortDropdown
                        value={formData.operationId}
                        onChange={(value) => handleInputChange("operationId", value)}
                        options={operationOptions}
                        theme={theme}
                        placeholder="Seleccione una operación"
                        className={`GM__${theme}-compact-dropdown`}
                        disabled={operationsLoading}
                      />
                    </div>
                  </div>

                  {/* Notificación para tipos que requieren operación */}
                  {requiresOperation && (
                    <div className={`GM__${theme}-operation-notice`}>
                      <span className={`GM__${theme}-notice-text`}>
                        ⚠️ Este tipo de vehículo requiere operación
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {!vehicle.is_trailer && vehicle.vehicle_type_id !== 'fc52271b-d9a9-47ac-b734-8aaa38bf5edf' && (
            <div className={`GM__${theme}-gps-section`}>
              <h3 className={`GM__${theme}-section-title`}>Configuración GPS Satelital</h3>
              <div className={`GM__${theme}-gps-content`}>
                <div className={`GM__${theme}-field-group`}>
                  <div className={`GM__${theme}-field-full`}>
                    <span className={`GM__${theme}-field-label`}>Proveedor GPS:</span>
                    <SortDropdown
                      value={formData.gpsProviderId}
                      onChange={(value) => handleInputChange("gpsProviderId", value)}
                      options={gpsProviderOptions}
                      theme={theme}
                      placeholder="Seleccione proveedor GPS"
                      className={`GM__${theme}-compact-dropdown`}
                      disabled={providersLoading}
                    />
                  </div>
                </div>
                
                {formData.gpsProviderId && (
                  <div className={`GM__${theme}-gps-info`}>
                    <span className={`GM__${theme}-info-icon`}>ℹ️</span>
                    <span className={`GM__${theme}-info-text`}>
                      El proveedor seleccionado se utilizará para el rastreo satelital del vehículo
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* NUEVA SECCIÓN: Gestión de Trailer (solo para cabezotes) */}
          {!vehicle.is_trailer && (
            <div className={`GM__${theme}-trailer-management-section`}>
              <h3 className={`GM__${theme}-section-title`}>Gestión de Trailer</h3>
              <div className={`GM__${theme}-trailer-management-content`}>
                <TrailerSelector
                  currentTrailerId={vehicle.relatedVehicle?.data?.id}
                  onTrailerSelect={handleTrailerSelection}
                  excludeVehicleId={vehicle.id}
                  theme={theme}
                  disabled={isAssociating || isDisassociating}
                />
                
                {selectedTrailer && selectedTrailer.id !== vehicle.relatedVehicle?.data?.id && (
                  <div className={`GM__${theme}-trailer-preview`}>
                    <h4 className={`GM__${theme}-trailer-preview-title`}>Trailer seleccionado:</h4>
                    <div className={`GM__${theme}-preview-info`}>
                      <div className={`GM__${theme}-preview-details`}>
                        <span className={`GM__${theme}-preview-plate`}>
                          {selectedTrailer.plate}
                        </span>
                        <span className={`GM__${theme}-preview-model`}>
                          {selectedTrailer.brand_name} {selectedTrailer.model} ({selectedTrailer.year})
                        </span>
                        {selectedTrailer.capacity && (
                          <span className={`GM__${theme}-preview-capacity`}>
                            {selectedTrailer.capacity} ton
                          </span>
                        )}
                      </div>
                      
                      {selectedTrailer.is_associated && (
                        <div className={`GM__${theme}-warning-section`}>
                          <span className={`GM__${theme}-warning-text`}>
                            ⚠️ Este trailer está asociado a otro vehículo ({selectedTrailer.associated_to_vehicle_plate})
                          </span>
                          <p className={`GM__${theme}-warning-description`}>
                            Si continúas, se desasociará automáticamente del vehículo actual.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedTrailer === null && vehicle.relatedVehicle?.data && (
                  <div className={`GM__${theme}-unassign-preview`}>
                    <h4 className={`GM__${theme}-unassign-preview-title`}>
                      ⚠️ Desasociar trailer actual
                    </h4>
                    <div className={`GM__${theme}-unassign-info`}>
                      <span>
                        Se desasociará el trailer <strong>{vehicle.relatedVehicle.data.plate}</strong> de este vehículo.
                      </span>
                    </div>
                  </div>
                )}

                {/* Botón de aplicar cambios de trailer */}
                {(selectedTrailer !== null || (selectedTrailer === null && vehicle.relatedVehicle?.data)) && (
                  <div className={`GM__${theme}-trailer-actions`}>
                    <button
                      type="button"
                      onClick={() => handleTrailerAssociation(false)}
                      className={`GM__${theme}-apply-trailer-button`}
                      disabled={isAssociating || isDisassociating}
                    >
                      {isAssociating || isDisassociating ? "Procesando..." : "Aplicar cambios de trailer"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedTrailer(vehicle.relatedVehicle?.data || null);
                        setTrailerConflictData(null);
                      }}
                      className={`GM__${theme}-cancel-trailer-button`}
                      disabled={isAssociating || isDisassociating}
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sección de contrato */}
          <div className={`GM__${theme}-contract-section`}>
            <h3 className={`GM__${theme}-section-title`}>Información de Contrato</h3>
            <div className={`GM__${theme}-contract-content`}>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>Fecha de inicio:</span>
                  <CustomDatePicker
                    value={formData.contractStartDate}
                    onChange={(date) => handleInputChange('contractStartDate', date)}
                    theme={theme}
                    className={`GM__${theme}-compact-date`}
                  />
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>Fecha de fin:</span>
                  <CustomDatePicker
                    value={formData.contractEndDate}
                    onChange={(date) => handleInputChange('contractEndDate', date)}
                    theme={theme}
                    className={`GM__${theme}-compact-date`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Grid de información */}
          <div className={`GM__${theme}-info-grid`}>
            {/* Información básica del vehículo - EDITABLE */}
            <div className={`GM__${theme}-info-card`}>
              <h3 className={`GM__${theme}-card-title`}>Información del vehículo</h3>
              <div className={`GM__${theme}-card-content`}>
                <div className={`GM__${theme}-field-group`}>
                  <div className={`GM__${theme}-field`}>
                    <span className={`GM__${theme}-field-label`}>Marca:</span>
                    <SortDropdown
                      value={formData.brandId}
                      onChange={(value) => handleInputChange('brandId', value)}
                      options={brandOptions}
                      theme={theme}
                      placeholder="Seleccione marca"
                      enableSearch={true}
                      enablePagination={true}
                      onSearch={brandsSearch.handleSearch}
                      searchPlaceholder="Buscar marca..."
                      itemsPerPage={brandsSearch.itemsPerPage}
                      totalItems={brandsSearch.totalItems}
                      currentPage={brandsSearch.currentPage}
                      onPageChange={brandsSearch.handlePageChange}
                      searchTerm={brandsSearch.searchTerm}
                      isLoading={brandsSearch.isLoading}
                      className={`GM__${theme}-compact-dropdown`}
                    />
                  </div>
                  <div className={`GM__${theme}-field`}>
                    <span className={`GM__${theme}-field-label`}>Modelo:</span>
                    <CustomInputField
                      value={formData.model}
                      onChange={(e) => handleInputChange('model', e.target.value)}
                      theme={theme}
                      className={`GM__${theme}-compact-input`}
                      placeholder="Ingrese modelo"
                    />
                  </div>
                </div>
                <div className={`GM__${theme}-field-group`}>
                  <div className={`GM__${theme}-field`}>
                    <span className={`GM__${theme}-field-label`}>Año:</span>
                    <CustomInputField
                      type="number"
                      value={formData.year}
                      onChange={(e) => handleInputChange('year', parseInt(e.target.value) || "")}
                      theme={theme}
                      className={`GM__${theme}-compact-input`}
                      placeholder="Año"
                      min="1900"
                      max="2030"
                    />
                  </div>
                  <div className={`GM__${theme}-field`}>
                    <span className={`GM__${theme}-field-label`}>Capacidad:</span>
                    <CustomInputField
                      type="number"
                      step="0.1"
                      value={formData.capacity}
                      onChange={(e) => handleInputChange('capacity', parseFloat(e.target.value) || "")}
                      theme={theme}
                      className={`GM__${theme}-compact-input`}
                      placeholder="Capacidad (ton)"
                      min="0"
                    />
                  </div>
                </div>
                <div className={`GM__${theme}-field-group`}>
                  <div className={`GM__${theme}-field`}>
                    <span className={`GM__${theme}-field-label`}>Color:</span>
                    <CustomInputField
                      value={formData.color}
                      onChange={(e) => handleInputChange('color', e.target.value)}
                      theme={theme}
                      className={`GM__${theme}-compact-input`}
                      placeholder="Color del vehículo"
                    />
                  </div>
                  <div className={`GM__${theme}-field`}>
                    <span className={`GM__${theme}-field-label`}>Nacionalidad:</span>
                    <SortDropdown
                      value={formData.nationalityId}
                      onChange={(value) => handleInputChange('nationalityId', value)}
                      options={nationalityOptions}
                      theme={theme}
                      placeholder="Seleccione país"
                      className={`GM__${theme}-compact-dropdown`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Selección de propietario - SOLO SELECCIÓN */}
            <div className={`GM__${theme}-info-card`}>
              <h3 className={`GM__${theme}-card-title`}>Asignación de propietario</h3>
              <div className={`GM__${theme}-card-content`}>
                {/* Campo editable: Selección de propietario */}
                <div className={`GM__${theme}-editable-section`}>
                  <div className={`GM__${theme}-field-group`}>
                    <div className={`GM__${theme}-field-full`}>
                      <span className={`GM__${theme}-field-label`}>Seleccionar propietario:</span>
                      <SortDropdown
                        value={formData.ownerId}
                        onChange={(value) => handleInputChange('ownerId', value)}
                        options={ownerOptions}
                        theme={theme}
                        placeholder="Seleccione propietario"
                        enableSearch={true}
                        enablePagination={true}
                        onSearch={ownersSearch.handleSearch}
                        searchPlaceholder="Buscar propietario..."
                        itemsPerPage={ownersSearch.itemsPerPage}
                        totalItems={ownersSearch.totalItems}
                        currentPage={ownersSearch.currentPage}
                        onPageChange={ownersSearch.handlePageChange}
                        searchTerm={ownersSearch.searchTerm}
                        isLoading={ownersSearch.isLoading}
                        className={`GM__${theme}-compact-dropdown`}
                      />
                    </div>
                  </div>
                </div>

                {/* Información del propietario actual (solo lectura) */}
                {vehicle.owner && (
                  <div className={`GM__${theme}-readonly-section`}>
                    <h4 className={`GM__${theme}-readonly-title`}>
                      Propietario actual (solo visualización):
                    </h4>
                    <div className={`GM__${theme}-readonly-grid`}>
                      <div className={`GM__${theme}-readonly-field`}>
                        <span className={`GM__${theme}-readonly-label`}>Nombre:</span>
                        <span className={`GM__${theme}-readonly-value`}>
                          {formatFullName(vehicle.owner)}
                        </span>
                      </div>
                      <div className={`GM__${theme}-readonly-field`}>
                        <span className={`GM__${theme}-readonly-label`}>Documento:</span>
                        <span className={`GM__${theme}-readonly-value`}>
                          {vehicle.owner.document_number || "No disponible"}
                        </span>
                      </div>
                      <div className={`GM__${theme}-readonly-field`}>
                        <span className={`GM__${theme}-readonly-label`}>Email:</span>
                        <span className={`GM__${theme}-readonly-value`}>
                          {vehicle.owner.email || "No disponible"}
                        </span>
                      </div>
                      <div className={`GM__${theme}-readonly-field`}>
                        <span className={`GM__${theme}-readonly-label`}>Tel. principal:</span>
                        <span className={`GM__${theme}-readonly-value`}>
                          {formatPhoneNumber(
                            vehicle.owner.primary_phone_code, 
                            vehicle.owner.primary_phone_number
                          )}
                        </span>
                      </div>
                      <div className={`GM__${theme}-readonly-field`}>
                        <span className={`GM__${theme}-readonly-label`}>Tel. secundario:</span>
                        <span className={`GM__${theme}-readonly-value`}>
                          {formatPhoneNumber(
                            vehicle.owner.secondary_phone_code, 
                            vehicle.owner.secondary_phone_number
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Selección de operador - SOLO SI NO ES TRAILER */}
            {shouldShowOperatorSection() && (
              <div className={`GM__${theme}-info-card`}>
                <h3 className={`GM__${theme}-card-title`}>Asignación de operador</h3>
                <div className={`GM__${theme}-card-content`}>
                  {/* Campo editable: Selección de operador */}
                  <div className={`GM__${theme}-editable-section`}>
                    <div className={`GM__${theme}-field-group`}>
                      <div className={`GM__${theme}-field-full`}>
                        <span className={`GM__${theme}-field-label`}>Seleccionar operador:</span>
                        <SortDropdown
                          value={formData.operatorId}
                          onChange={(value) => handleInputChange('operatorId', value)}
                          options={operatorOptions}
                          theme={theme}
                          placeholder="Seleccione operador"
                          enableSearch={true}
                          enablePagination={true}
                          onSearch={operatorsSearch.handleSearch}
                          searchPlaceholder="Buscar operador..."
                          itemsPerPage={operatorsSearch.itemsPerPage}
                          totalItems={operatorsSearch.totalItems}
                          currentPage={operatorsSearch.currentPage}
                          onPageChange={operatorsSearch.handlePageChange}
                          searchTerm={operatorsSearch.searchTerm}
                          isLoading={operatorsSearch.isLoading}
                          className={`GM__${theme}-compact-dropdown`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Información del operador actual (solo lectura) */}
                  {vehicle.operator && (
                    <div className={`GM__${theme}-readonly-section`}>
                      <h4 className={`GM__${theme}-readonly-title`}>
                        Operador actual (solo visualización):
                      </h4>
                      <div className={`GM__${theme}-readonly-grid`}>
                        <div className={`GM__${theme}-readonly-field`}>
                          <span className={`GM__${theme}-readonly-label`}>Nombre:</span>
                          <span className={`GM__${theme}-readonly-value`}>
                            {formatFullName(vehicle.operator)}
                          </span>
                        </div>
                        <div className={`GM__${theme}-readonly-field`}>
                          <span className={`GM__${theme}-readonly-label`}>Documento:</span>
                          <span className={`GM__${theme}-readonly-value`}>
                            {vehicle.operator.document_number || "No disponible"}
                          </span>
                        </div>
                        <div className={`GM__${theme}-readonly-field`}>
                          <span className={`GM__${theme}-readonly-label`}>Email:</span>
                          <span className={`GM__${theme}-readonly-value`}>
                            {vehicle.operator.email || "No disponible"}
                          </span>
                        </div>
                        <div className={`GM__${theme}-readonly-field`}>
                          <span className={`GM__${theme}-readonly-label`}>Tel. principal:</span>
                          <span className={`GM__${theme}-readonly-value`}>
                            {formatPhoneNumber(
                              vehicle.operator.primary_phone_code, 
                              vehicle.operator.primary_phone_number
                            )}
                          </span>
                        </div>
                        <div className={`GM__${theme}-readonly-field`}>
                          <span className={`GM__${theme}-readonly-label`}>Tel. secundario:</span>
                          <span className={`GM__${theme}-readonly-value`}>
                            {formatPhoneNumber(
                              vehicle.operator.secondary_phone_code, 
                              vehicle.operator.secondary_phone_number
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Información de contacto del vehículo - EDITABLE */}
            <div className={`GM__${theme}-info-card`}>
              <h3 className={`GM__${theme}-card-title`}>Contacto del vehículo</h3>
              <div className={`GM__${theme}-card-content`}>
                <div className={`GM__${theme}-field-group`}>
                  <div className={`GM__${theme}-field-full`}>
                    <span className={`GM__${theme}-field-label`}>Teléfono principal del vehículo:</span>
                    <div className={`GM__${theme}-phone-group`}>
                      <SortDropdown
                        value={formData.primaryPhoneCountryId}
                        onChange={(value) => handleInputChange('primaryPhoneCountryId', value)}
                        options={countryOptions}
                        theme={theme}
                        placeholder="Código"
                        type="phone"
                        className={`GM__${theme}-phone-code`}
                      />
                      <CustomInputField
                        value={formData.primaryPhoneNumber}
                        onChange={(e) => handleInputChange('primaryPhoneNumber', e.target.value)}
                        theme={theme}
                        className={`GM__${theme}-phone-input`}
                        placeholder="Número de teléfono"
                        type="tel"
                      />
                    </div>
                  </div>
                </div>
                <div className={`GM__${theme}-field-group`}>
                  <div className={`GM__${theme}-field-full`}>
                    <span className={`GM__${theme}-field-label`}>Teléfono secundario del vehículo:</span>
                    <div className={`GM__${theme}-phone-group`}>
                      <SortDropdown
                        value={formData.secondaryPhoneCountryId}
                        onChange={(value) => handleInputChange('secondaryPhoneCountryId', value)}
                        options={countryOptions}
                        theme={theme}
                        placeholder="Código"
                        type="phone"
                        className={`GM__${theme}-phone-code`}
                      />
                      <CustomInputField
                        value={formData.secondaryPhoneNumber}
                        onChange={(e) => handleInputChange('secondaryPhoneNumber', e.target.value)}
                        theme={theme}
                        className={`GM__${theme}-phone-input`}
                        placeholder="Número de teléfono"
                        type="tel"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Información adicional del vehículo */}
            <div className={`GM__${theme}-info-card`}>
              <h3 className={`GM__${theme}-card-title`}>Información adicional</h3>
              <div className={`GM__${theme}-card-content`}>
                <div className={`GM__${theme}-field-group`}>
                  <div className={`GM__${theme}-field`}>
                    <span className={`GM__${theme}-field-label`}>Operación:</span>
                    <span className={`GM__${theme}-readonly-value`}>
                      {vehicle.operation_name || "No asignada"}
                    </span>
                  </div>
                  <div className={`GM__${theme}-field`}>
                    <span className={`GM__${theme}-field-label`}>Tipo de categoría:</span>
                    <span className={`GM__${theme}-readonly-value`}>
                      {vehicle.type_category || "No disponible"}
                    </span>
                  </div>
                </div>
                <div className={`GM__${theme}-field-group`}>
                  <div className={`GM__${theme}-field`}>
                    <span className={`GM__${theme}-field-label`}>Creado:</span>
                    <span className={`GM__${theme}-readonly-value`}>
                      {formatDate(vehicle.created_at)}
                    </span>
                  </div>
                  <div className={`GM__${theme}-field`}>
                    <span className={`GM__${theme}-field-label`}>Actualizado:</span>
                    <span className={`GM__${theme}-readonly-value`}>
                      {formatDate(vehicle.updated_at)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* MODAL DE CONFIRMACIÓN PARA TRAILER */}
      <TrailerAssociationModal
        open={showTrailerModal}
        onClose={() => {
          setShowTrailerModal(false);
          setTrailerConflictData(null);
          setSelectedTrailer(vehicle.relatedVehicle?.data || null);
        }}
        onConfirm={handleTrailerAssociation}
        trailerData={selectedTrailer}
        conflictData={trailerConflictData}
        theme={theme}
        isLoading={isAssociating}
      />
    </div>
  );
};

// PROPIEDADES ACTUALIZADAS
VehicleProfileEdit.propTypes = {
  vehicle: PropTypes.shape({
    id: PropTypes.string.isRequired,
    plate: PropTypes.string,
    vehicle_type_id: PropTypes.string,
    brand_id: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.number,
    color: PropTypes.string,
    capacity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    nationality_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    primary_phone_number: PropTypes.string,
    primary_phone_country_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    second_phone: PropTypes.string,
    secondary_phone_country_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    contract_start_date: PropTypes.string,
    contract_end_date: PropTypes.string,
    owner_id: PropTypes.string,
    operator_id: PropTypes.string,
    operation_id: PropTypes.string,
    belongs_to_operation: PropTypes.bool,
    is_trailer: PropTypes.bool,
    operation_name: PropTypes.string,
    type_category: PropTypes.string,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
    owner: PropTypes.object,
    operator: PropTypes.object,
    relatedVehicle: PropTypes.object, // NUEVO
  }).isRequired,
  theme: PropTypes.oneOf(['dark', 'light']).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isRelatedVehicle: PropTypes.bool
};

export default VehicleProfileEdit;